import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home,
  ShoppingCart,
  Business,
  Description,
  VolunteerActivism,
  Favorite,
  LocalHospital,
} from '@mui/icons-material';

export const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Scroll to top when navigating to a new page (pathname change)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Home', path: '/', icon: <Home /> },
    { text: 'Food Parcels', path: '/food-parcels', icon: <ShoppingCart /> },
    { text: 'Sponsors', path: '/sponsors', icon: <Business /> },
    { text: 'T&C', path: '/terms', icon: <Description /> },
    { text: 'Volunteer', path: '/volunteer', icon: <VolunteerActivism /> },
    { text: 'Donations', path: '/donations', icon: <Favorite /> },
    { text: 'New Life Community Care', path: '/community-care', icon: <LocalHospital /> },
  ];

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              color: location.pathname === item.path ? 'primary.main' : 'text.primary',
              backgroundColor: location.pathname === item.path ? 'primary.light' : 'transparent',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.main',
              },
            }}
          >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              {item.icon}
            </Box>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 'bold' : 'normal',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <div className="min-h-screen bg-secondary-100">
      <AppBar 
        position="static" 
        elevation={2}
        sx={{ 
          bgcolor: 'white',
          color: 'text.primary',
          borderBottom: '2px solid',
          borderColor: 'primary.main'
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, justifyContent: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                    backgroundColor: location.pathname === item.path ? 'primary.light' : 'transparent',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>

      <main className="container mx-auto px-4 py-8">
        <Outlet />
      </main>

      <footer className="bg-white shadow mt-8">
        <div className="container mx-auto px-4 py-3">
          <div className="text-center text-gray-600 text-sm">
            <div className="flex justify-center mb-2">
              <img
                src="/images/positivessl_trust_seal_lg_222x54.png"
                alt="Secured by PositiveSSL"
                width={222}
                height={54}
              />
            </div>
            <p>Copyright 2025 - New Life Community Care Trading as New Life Foodbarn. D.G.R. Benevolent Institution / Charity - ABN - 72145513711</p>
          </div>
        </div>
      </footer>
    </div>
  );
}; 